import { useSyncExternalStore } from "react";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useLabsExperiment } from "./labExperiment";
import type { LabOrder, LabReport, LabReferenceInterval } from "@/types/labs";

export const LAB_PREVIEW_ID = "staging-preview";
export const labPreviewStages = [
  ["actual", "Actual account"],
  ["empty", "No test yet"],
  ["pendingFulfillment", "Preparing kit"],
  ["inTransitToPatient", "Kit on its way"],
  ["atPatient", "Kit delivered"],
  ["inTransitToLab", "Sample returning"],
  ["atLab", "At the laboratory"],
  ["resultsReady", "Results available"],
  ["problem", "Needs attention"],
] as const;
export type LabPreviewStage = (typeof labPreviewStages)[number][0];
const listeners = new Set<() => void>();
const key = (owner: string) => `system.labs.preview.${owner}`;
const notify = () => listeners.forEach((listener) => listener());
if (typeof window !== "undefined") window.addEventListener("storage", notify);
function read(owner?: string): LabPreviewStage {
  if (import.meta.env.VITE_APP_ENV !== "staging" || !owner) return "actual";
  try {
    const value = localStorage.getItem(key(owner));
    return labPreviewStages.some(([stage]) => stage === value)
      ? (value as LabPreviewStage)
      : "actual";
  } catch {
    return "actual";
  }
}
export function useLabPreview() {
  const { session } = useAuth();
  const { enabled } = useLabsExperiment();
  const owner = session?.user.id;
  const stored = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => read(owner),
    () => "actual" as LabPreviewStage,
  );
  const available =
    import.meta.env.VITE_APP_ENV === "staging" && enabled && Boolean(owner);
  return {
    available,
    stage: available ? stored : ("actual" as LabPreviewStage),
    setStage(stage: LabPreviewStage) {
      if (!available || !owner) return;
      localStorage.setItem(key(owner), stage);
      notify();
    },
  };
}
const exampleValues = [
  ["Lipoprotein(a)", "18 nmol/L"],
  ["Apolipoprotein B", "78 mg/dL"],
  ["HS-CRP", "0.8 mg/L"],
  ["Hemoglobin A1C", "5.2 %"],
  ["Total testosterone", "520 ng/dL"],
  ["Creatinine", "0.9 mg/dL"],
  ["Alkaline phosphatase", "72 U/L"],
  ["Cholesterol", "178 mg/dL"],
  ["HDL", "58 mg/dL"],
  ["Cholesterol/HDL ratio", "3.1"],
  ["Triglycerides", "90 mg/dL"],
  ["VLDL", "18 mg/dL"],
  ["LDL", "102 mg/dL"],
  ["LDL/HDL ratio", "1.8"],
];
function exampleReference(name: string): LabReferenceInterval | undefined {
  const bounds: Record<string, [number, number, string]> = {
    "Total testosterone": [249, 836, "ng/dL"],
    "Cholesterol/HDL ratio": [1.5, 4, ""],
    "LDL/HDL ratio": [0, 3.6, ""],
  };
  const range = bounds[name];
  if (!range) return undefined;
  return {
    low: range[0],
    high: range[1],
    unit: range[2],
    low_inclusive: true,
    high_inclusive: true,
    source: "laboratory",
    origin: "source_report",
    label: "Example laboratory reference range",
  };
}
export function previewLabOrders(stage: LabPreviewStage): LabOrder[] {
  if (stage === "actual" || stage === "empty") return [];
  const date = "2026-09-14T12:00:00Z";
  const orders: LabOrder[] = [
    {
      id: LAB_PREVIEW_ID,
      order_number: "DEMO",
      panel_code: "Y499",
      product_name: "At-home blood test",
      created_at: date,
      amount_paid_cents: 9900,
      currency: "usd",
      payment_status: "paid",
      authorization_status: "not_required",
      fulfillment_status: stage,
      status_changed_at: date,
      bundles: [
        {
          id: "demo-kit",
          status: stage === "resultsReady" ? "atLab" : stage,
          status_changed_at: date,
          specimen_ids: [],
          outbound_tracking: null,
          return_tracking: null,
        },
      ],
      results:
        stage === "resultsReady"
          ? [
              {
                id: "demo-report",
                created_at: date,
                collected_at: "2026-09-12",
                status: "final",
                quality_status: "received",
                observations: exampleValues.map(([name, value_text]) => ({
                  name,
                  value_text,
                  reference_context: {
                    age_at_collection: 30,
                    assigned_sex: "male",
                  },
                  reference_interval: exampleReference(name),
                  interpretation:
                    name === "LDL"
                      ? ["HIGH"]
                      : name === "Total testosterone"
                        ? []
                        : ["NORMAL"],
                  notes: [],
                })),
                report_available: true,
                report_count: 1,
                report_content_type: "text/plain",
                notes: [
                  "Synthetic staging example. Not a patient result. Values and flags are for layout review only.",
                  "Range comparisons use an example 30-year-old male profile.",
                ],
              },
            ]
          : [],
      issue:
        stage === "problem"
          ? "Example: contact your care team in Clinic about your kit."
          : null,
      recollection_message: null,
    },
  ];
  if (stage === "resultsReady") {
    const current = orders[0];
    orders.push({
      ...current,
      id: `${LAB_PREVIEW_ID}-previous`,
      created_at: "2026-06-12T12:00:00Z",
      results: [
        {
          ...current.results[0],
          id: "demo-report-previous",
          created_at: "2026-06-14T12:00:00Z",
          collected_at: "2026-06-12",
          report_available: false,
          report_count: 0,
          observations: current.results[0].observations.map((item) => ({
            ...item,
            value_text:
              item.name === "LDL"
                ? "114 mg/dL"
                : item.name === "Cholesterol"
                  ? "191 mg/dL"
                  : item.name === "Hemoglobin A1C"
                    ? "5.4 %"
                    : item.value_text,
          })),
        },
      ],
    });
  }
  return orders;
}
export function previewLabReport(): LabReport {
  return {
    title: "Example laboratory report — staging only",
    content_type: "text/plain",
    content_text:
      "SYNTHETIC STAGING PREVIEW — NOT A PATIENT RESULT\n\n" +
      exampleValues.map(([name, value]) => `${name}: ${value}`).join("\n"),
  };
}
